'use client'

import React from 'react'
import { ArrowUpDown, Check, ChevronDown, ExternalLink, Wallet as WalletIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ESPO_URL } from '@/utils/constants'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import {
	bestQuoteExactOut,
	SFAMM_VIA_ROUTER,
	BTC_ID,
	formatAmount,
	FRBTC_ID,
	FRUSD_ID,
	parseAmount,
	sideLabels,
	type PoolToken
} from '../lib/amm'
import {
	btcSideSats,
	carryAcrossFlip,
	convertEntry,
	entryUnits,
	groupWhole,
	nextUnit,
	pctEntry,
	prefillFor,
	toAmountString,
	typedNumber,
	unitDecimals,
	type EntryUnit
} from '../lib/entry'
import { buyFunding } from '../lib/funding'
import { usdCompact } from '../lib/format'
import { sortByUsdValue } from '../lib/position-sort'
import { useAlkanes } from '../lib/store'
import { Change } from './asset-row'
import SwapPanel, { type BroadcastInfo } from './swap-panel'
import { TokenIcon } from './token-select'
import TwetchDoor from './twetch-door'

/// One-tap buy sizes, in the entry unit itself. They fill the SAME input a
/// keyboard would and go through the same quote, guards and review — a
/// shortcut to the amount, never a shortcut past the confirmation.
const BTC_QUICK = ['0.001', '0.005', '0.01'] as const

/// The desktop trade surface — fomo web's right rail, wired to this app's one
/// audited engine, which now renders INSIDE the box: as the user types, the
/// inline invoice materializes beneath the amount, and the CTA advances the
/// SAME box to the frozen confirm stage. No module switch — and no change to
/// the road to a signature: quote, guards, frozen review, real-PSBT fee, and
/// nothing ever signs on a first click.
///
/// Entry is BTC-first on buys (sats-exact pass-through) and token-first on
/// sells; the ⇅ cycles the other units the live rates can convert.
export default function TradeBox() {
	const asset = useAlkanes((s) => s.asset)
	const btcUsd = useAlkanes((s) => s.btcUsd)
	const balances = useAlkanes((s) => s.balances)
	const balancesUsd = useAlkanes((s) => s.balancesUsd)
	const balancesUnavailable = useAlkanes((s) => s.balancesUnavailable)
	const balanceRows = useAlkanes((s) => s.balanceRows)
	const tokens = useAlkanes((s) => s.tokens)
	const pools = useAlkanes((s) => s.pools)
	const routerFeePer1000 = useAlkanes((s) => s.routerFeePer1000)
	const sfammPools = useAlkanes((s) => s.sfammPools)
	const routeOpts = React.useMemo(
		() => ({ sfammPools, sfammViaRouter: SFAMM_VIA_ROUTER }),
		[sfammPools]
	)
	const stats = useAlkanes((s) => s.assetStats)
	const selectSell = useAlkanes((s) => s.selectSell)
	const selectBuy = useAlkanes((s) => s.selectBuy)
	const tradeIntent = useAlkanes((s) => s.tradeIntent)
	const setTradeIntent = useAlkanes((s) => s.setTradeIntent)
	const navigate = useAlkanes((s) => s.navigate)
	const watchAddress = useAlkanes((s) => s.watchAddress)
	const wrapSwapTopup = useAlkanes((s) => s.wrapSwapTopup)
	const loggedIn = useLoggedIn()
	// Reads behave logged-in under watch mode; only signing stays gated.
	const canRead = Boolean(loggedIn) || watchAddress !== null
	const balancesReady = canRead && !balancesUnavailable

	const [side, setSide] = React.useState<'buy' | 'sell'>('buy')
	const [raw, setRaw] = React.useState('')
	const [unitChoice, setUnitChoice] = React.useState<EntryUnit | null>(null)
	const [reviewOpen, setReviewOpen] = React.useState(false)
	const [reviewRequest, setReviewRequest] = React.useState(0)
	const [done, setDone] = React.useState<{ info: BroadcastInfo; usd: number | null } | null>(null)
	const [showDust, setShowDust] = React.useState(false)
	const [expandedPosition, setExpandedPosition] = React.useState<string | null>(null)

	const symbol = asset?.name || asset?.id || ''
	const price = asset?.price_usd ?? null
	const ctx = React.useMemo(() => ({ btcUsd, price }), [btcUsd, price])
	const units = entryUnits(side, ctx)
	const unit: EntryUnit = unitChoice !== null && units.includes(unitChoice) ? unitChoice : units[0]

	// Keep the pair pointed at this box's side. Buys are frBTC-FIRST: held
	// frBTC pays whenever it covers the buy (a plain swap — no wrap fee, one
	// transaction); native BTC wraps in as the top-up valve otherwise. Sells
	// stop at frBTC, 1:1 with BTC — proceeds read in BTC and stay spendable
	// as the next buy's funding. frBTC's own screen keeps the signer
	// wrap/unwrap doors (it cannot trade against itself).
	const assetToken = React.useMemo(
		() => (asset ? tokens.find((t) => t.id === asset.id) ?? null : null),
		[tokens, asset]
	)
	const btcToken = tokens[0] ?? null
	const frbtcToken = React.useMemo(
		(): PoolToken =>
			tokens.find((t) => t.id === FRBTC_ID) ?? { id: FRBTC_ID, name: 'frBTC', icon: null },
		[tokens]
	)

	const typed = typedNumber(raw)

	// The BTC-side input a buy needs, for the funding decision. BTC entry IS
	// the input; USD converts at the live price; token entry inverts the live
	// route the same way the panel's exact-out quote will.
	const neededIn: bigint | null = React.useMemo(() => {
		if (side !== 'buy' || typed <= 0 || !asset) {
			return null
		}
		if (unit !== 'token') {
			return btcSideSats(unit, raw, ctx)
		}
		const want = parseAmount(toAmountString(typed, 8))
		if (!want || want <= 0n) {
			return null
		}
		// frUSD trades on the curve pool; the AMM exact-out here would size
		// funding off the $43 dust pool. Estimate from the live prices (both
		// curve-derived) with a small pad — the panel's real quote decides.
		if (asset.id === FRUSD_ID) {
			if (price === null || btcUsd === null || btcUsd <= 0) {
				return null
			}
			const sats = Math.ceil((typed * price * 1.005 * 1e8) / btcUsd)
			return sats > 0 ? BigInt(sats) : null
		}
		const q = bestQuoteExactOut(pools, FRBTC_ID, asset.id, want, routerFeePer1000, routeOpts)
		return q ? q.amountIn : null
	}, [side, typed, unit, raw, ctx, pools, routerFeePer1000, routeOpts, asset, price, btcUsd])

	const assetIsFrbtc = asset?.id === FRBTC_ID
	const assetIsFrusd = asset?.id === FRUSD_ID
	const frbtcBal = assetIsFrbtc ? 0n : balances.get(FRBTC_ID) ?? 0n
	const btcBal = balances.get(BTC_ID) ?? 0n
	// Third rung admitted only while the API capability flag holds — the
	// panel verifies it on the first real build and downgrades the session.
	// frUSD buys fund from held frBTC only: the BTC door is wrapSwap, whose
	// AMM leg would route frUSD through the dust pool. Wrap first, then buy.
	const funding = buyFunding(
		frbtcBal,
		assetIsFrusd ? 0n : btcBal,
		neededIn,
		wrapSwapTopup && !assetIsFrusd
	)

	React.useEffect(() => {
		if (!assetToken || !btcToken) {
			return
		}
		if (side === 'buy') {
			selectSell(funding.source === 'frbtc' ? frbtcToken : btcToken)
			selectBuy(assetToken)
		} else {
			selectSell(assetToken)
			selectBuy(assetIsFrbtc ? btcToken : frbtcToken)
		}
	}, [side, assetToken, btcToken, frbtcToken, assetIsFrbtc, funding.source, selectSell, selectBuy])

	// Spendable base, computed in bigint: with the top-up rung the two buy
	// balances genuinely combine (the shortfall wraps, held frBTC rides the
	// swap leg); without it, the larger single funding path. Sells use the
	// held token.
	const spendableRaw =
		side === 'buy' ? funding.spendable : asset ? balances.get(asset.id) ?? 0n : 0n
	const availableUsd =
		side === 'buy'
			? btcUsd !== null
				? (Number(spendableRaw) / 1e8) * btcUsd
				: null
			: asset
			? balancesUsd.get(asset.id) ?? null
			: null
	// An instant-action chip elsewhere named this asset: consume the intent
	// once, fill the amount in the side's own unit, and ask the panel to
	// advance to the frozen review as soon as the plan can freeze.
	React.useEffect(() => {
		if (!tradeIntent || !asset || tradeIntent.assetId !== asset.id) {
			return
		}
		setTradeIntent(null)
		// This is an external-store command, not derived render state: consuming
		// it intentionally initializes the local editor as one atomic action.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setSide(tradeIntent.side)
		setUnitChoice(null)
		if (tradeIntent.side === 'buy' && tradeIntent.btc !== undefined) {
			const s = toAmountString(tradeIntent.btc, 8)
			if (s) {
				setRaw(s)
				setReviewRequest((n) => n + 1)
			}
		} else if (tradeIntent.side === 'sell' && tradeIntent.pct !== undefined) {
			const held = balances.get(asset.id) ?? 0n
			const s = pctEntry('sell', 'token', held, tradeIntent.pct, ctx)
			if (s) {
				setRaw(s)
				setReviewRequest((n) => n + 1)
			}
		}
	}, [tradeIntent, asset, balances, ctx, setTradeIntent])

	const resetEntry = React.useCallback(() => {
		setRaw('')
		setDone(null)
	}, [])

	// Account switched: the typed amount and receipt were the old account's.
	const address = useAlkanes((st) => st.address)
	const lastAddress = React.useRef(address)
	React.useEffect(() => {
		if (lastAddress.current !== address) {
			lastAddress.current = address
			resetEntry()
		}
	}, [address, resetEntry])

	// Asset changed: the typed amount and the done card belonged to the OLD
	// asset; everything else on the rail (side, dust expansion, open
	// position) survives — the box is no longer remounted per asset.
	const lastAssetId = React.useRef(asset?.id ?? null)
	React.useEffect(() => {
		if (asset?.id && lastAssetId.current !== asset.id) {
			lastAssetId.current = asset.id
			setRaw('')
			setDone(null)
		}
	}, [asset?.id])

	// The input DISPLAYS thousands separators while `raw` stays the plain
	// number (arch-swap's caret bookkeeping: track the caret as a count of
	// non-comma characters and restore it after the value re-renders with
	// commas, so typing into the middle never jumps the cursor).
	const amountRef = React.useRef<HTMLInputElement | null>(null)
	const pendingCaret = React.useRef<number | null>(null)
	const display = groupWhole(raw)
	React.useLayoutEffect(() => {
		const target = pendingCaret.current
		const el = amountRef.current
		if (target === null || !el) {
			return
		}
		pendingCaret.current = null
		let seen = 0
		let at = display.length
		for (let i = 0; i < display.length; i++) {
			if (display[i] !== ',') {
				seen++
				if (seen === target) {
					at = i + 1
					break
				}
			}
		}
		if (target === 0) {
			at = 0
		}
		el.setSelectionRange(at, at)
	}, [display])

	if (!asset) {
		return null
	}

	const prefill = prefillFor(side, unit, raw, ctx)
	const usdValue = convertEntry(typed, unit, 'usd', ctx)

	const setSideAndReset = (s: 'buy' | 'sell') => {
		if (s !== side) {
			setSide(s)
			setRaw('')
			setUnitChoice(null)
		}
	}

	// The figure yields size before it yields digits: past ten characters the
	// font steps down so the full amount stays visible in the card.
	const amountSize = display.length > 14 ? 'text-xl' : display.length > 10 ? 'text-2xl' : 'text-3xl'

	const editRaw = (v: string, caretAt?: number | null) => {
		const plain = v.replace(/,/g, '')
		const re = new RegExp(`^\\d*\\.?\\d{0,${unitDecimals(unit)}}$`)
		if (re.test(plain) && plain.length <= 12) {
			if (caretAt !== undefined && caretAt !== null) {
				let n = 0
				for (let i = 0; i < caretAt; i++) {
					if (v[i] !== ',') {
						n++
					}
				}
				pendingCaret.current = n
			}
			setRaw(plain)
		}
	}

	const flipUnit = () => {
		if (units.length < 2) {
			return
		}
		const to = nextUnit(unit, side, ctx)
		setRaw((r) => carryAcrossFlip(r, unit, to, ctx))
		setUnitChoice(to)
	}

	// A quick size, re-expressed for whatever unit is being typed.
	const quickEntry = (btc: string): string => {
		if (unit === 'btc') {
			return btc
		}
		const converted = convertEntry(Number.parseFloat(btc), 'btc', unit, ctx)
		return converted !== null && converted > 0 ? toAmountString(converted, unitDecimals(unit)) : ''
	}

	// The converter: the same amount in the next unit of the ⇅ cycle.
	const converterUnit = units.length > 1 ? nextUnit(unit, side, ctx) : null
	// USD left the entry ladder; it lives on as the display conversion under
	// the amount — the figure a person actually reasons in. When no dollar
	// price exists, the other entry unit stands in.
	const usdEst = convertEntry(typed, unit, 'usd', ctx)
	const converted = converterUnit !== null ? convertEntry(typed, unit, converterUnit, ctx) : null
	const converterText =
		usdEst !== null
			? `≈ $${toAmountString(usdEst, 2) || '0'}`
			: converterUnit === null || converted === null
			? '—'
			: converterUnit === 'btc'
			? `≈ ${toAmountString(converted, 8) || '0'} BTC`
			: `≈ ${toAmountString(converted, converted >= 1 ? 2 : 6) || '0'} ${symbol}`

	return (
		<div
			className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto pl-1"
			data-testid="alkanes-trade-box"
		>
			<div className="rounded-2xl bg-card p-3">
				{done ? (
					<DoneCard info={done.info} usd={done.usd} side={side} onReset={resetEntry} />
				) : (
					<React.Fragment>
						{/* Entry chrome — collapses while the frozen review is up so
						    the confirm stage owns the box without a module switch. */}
						<div
							className={cn('alkanes-reveal', !reviewOpen && 'alkanes-reveal-open')}
							aria-hidden={reviewOpen}
						>
							<div className="min-h-0 overflow-hidden">
								{/* Buy / Sell segmented — the SIDE keeps its green/red
								    identity, but as tints under the AA-safe ink tokens
								    (the solid fills put white at 2.3:1). The money CTA
								    below stays the brand accent: one accent, and
								    green/red reserved for deltas and side identity. */}
								<div role="group" aria-label="Trade side" className="grid grid-cols-2 gap-1.5">
									{(['buy', 'sell'] as const).map((s) => {
										const on = side === s
										return (
											<button
												key={s}
												type="button"
												aria-pressed={on}
												data-testid={`alkanes-box-${s}`}
												onClick={() => setSideAndReset(s)}
												className={cn(
													'h-11 rounded-xl text-sm font-bold capitalize transition-colors',
													on && s === 'buy' && 'bg-success/15 text-success-ink',
													on && s === 'sell' && 'bg-destructive/15 text-destructive-ink',
													!on && 'bg-secondary text-muted-foreground hover:text-foreground'
												)}
											>
												{s === 'buy' ? sideLabels(asset?.id).buy : sideLabels(asset?.id).sell}
											</button>
										)
									})}
								</div>

								{/* The amount card — jup.ag's grammar in OW's identity: the
								    header says WHAT the number means and carries the
								    CLICKABLE wallet balance (tap = spend it all), the unit
								    chip on the left cycles entry units (replacing the old
								    converter button), the amount reads large and
								    right-aligned, and the live conversion sits beneath it.
								    Focus draws the brand outline. The audited entry
								    machinery is untouched — same input, same ids. */}
								<div
									className={cn(
										'mt-3 rounded-2xl border border-border/50 bg-secondary/30 px-4 pb-3 pt-3 transition-colors',
										'focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20'
									)}
								>
									<div className="flex min-h-6 items-center justify-between gap-2 text-xs">
										<span className="shrink-0 font-medium text-muted-foreground">
											{side === 'buy'
												? unit === 'token'
													? `You ${sideLabels(asset?.id).buy}`
													: 'You pay'
												: `You ${sideLabels(asset?.id).sell}`}
										</span>
										{balancesUnavailable ? (
											<span className="truncate text-muted-foreground">Balances unavailable</span>
										) : canRead ? (
											<button
												type="button"
												data-testid="alkanes-box-balance"
												disabled={!balancesReady || spendableRaw <= 0n}
												onClick={() => {
													const v = pctEntry(side, unit, spendableRaw, 100, ctx)
													if (v !== '') {
														setRaw(v)
													}
												}}
												title="Use the full balance"
												className="alkanes-touch flex min-w-0 items-center gap-1 rounded-md px-1 py-0.5 tabular-nums text-muted-foreground transition-colors hover:text-primary disabled:opacity-50 disabled:hover:text-muted-foreground"
											>
												<WalletIcon className="size-3 shrink-0" aria-hidden="true" />
												<span className="truncate">
													{formatAmount(spendableRaw, 8)} {side === 'buy' ? 'BTC' : symbol}
												</span>
												{availableUsd !== null && (
													<span className="shrink-0 text-muted-foreground/70">
														{usdCompact(availableUsd)}
													</span>
												)}
											</button>
										) : (
											<span className="truncate text-muted-foreground">
												Connect a wallet to trade
											</span>
										)}
									</div>
									<div className="mt-1.5 flex items-center gap-2">
										<button
											type="button"
											data-testid="alkanes-box-flip"
											onClick={units.length > 1 ? flipUnit : undefined}
											disabled={units.length <= 1}
											className={cn(
												'alkanes-touch flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-card py-1 pl-1.5 pr-2 text-sm font-bold transition-colors',
												units.length > 1 && 'hover:border-primary/40 hover:bg-secondary'
											)}
										>
											{unit === 'token' && assetToken ? (
												<TokenIcon token={assetToken} size={20} />
											) : btcToken ? (
												<TokenIcon token={btcToken} size={20} />
											) : null}
											<span className="max-w-[72px] truncate">
												{unit === 'btc' ? 'BTC' : symbol}
											</span>
											{units.length > 1 && (
												<React.Fragment>
													<ArrowUpDown
														className="size-3 text-muted-foreground"
														aria-hidden="true"
													/>
													<span className="sr-only">
														Switch to entering the amount in{' '}
														{converterUnit === 'btc' ? 'BTC' : symbol}
													</span>
												</React.Fragment>
											)}
										</button>
										<input
											ref={amountRef}
											data-testid="alkanes-box-amount"
											inputMode="decimal"
											autoComplete="off"
											placeholder="0"
											aria-label={`Amount in ${unit === 'btc' ? 'BTC' : symbol} to ${side}`}
											value={display}
											onChange={(e) => editRaw(e.target.value, e.target.selectionStart)}
											className={cn(
												'h-12 w-full min-w-0 bg-transparent text-right font-bold tabular-nums outline-none placeholder:text-muted-foreground/50',
												amountSize
											)}
										/>
									</div>
									<div className="mt-0.5 min-h-4 text-right text-xs tabular-nums text-muted-foreground">
										{raw !== '' && converterUnit !== null ? converterText : '\u00a0'}
									</div>
								</div>

								{/* Quick sizes: BTC denominations on buys, % of the held
								    token on sells — bigint math, so 100% is exact. */}
								<div className="mt-2 flex gap-1.5">
									{side === 'buy' ? (
										<React.Fragment>
											{BTC_QUICK.map((btc) => {
												const v = quickEntry(btc)
												return (
													<button
														key={btc}
														type="button"
														data-testid={`alkanes-box-chip-${btc}`}
														disabled={v === ''}
														onClick={() => setRaw(v)}
														className={cn(
															'h-9 flex-1 rounded-lg bg-secondary text-xs font-bold tabular-nums transition-colors hover:bg-secondary/70 disabled:opacity-40',
															raw !== '' &&
																raw === v &&
																'bg-foreground text-background hover:bg-foreground'
														)}
													>
														₿{btc}
													</button>
												)
											})}
											<button
												type="button"
												data-testid="alkanes-box-max"
												disabled={!balancesReady || spendableRaw <= 0n}
												onClick={() => {
													const v = pctEntry('buy', unit, spendableRaw, 100, ctx)
													if (v !== '') {
														setRaw(v)
													}
												}}
												className="h-9 flex-1 rounded-lg bg-secondary text-xs font-bold transition-colors hover:bg-secondary/70 disabled:opacity-40"
											>
												Max
											</button>
										</React.Fragment>
									) : (
										[25, 50, 75, 100].map((pct) => (
											<button
												key={pct}
												type="button"
												data-testid={`alkanes-box-pct-${pct}`}
												disabled={!balancesReady || spendableRaw <= 0n}
												onClick={() => {
													const v = pctEntry('sell', unit, spendableRaw, pct, ctx)
													if (v !== '') {
														setRaw(v)
													}
												}}
												className="h-9 flex-1 rounded-lg bg-secondary text-xs font-bold tabular-nums transition-colors hover:bg-secondary/70 disabled:opacity-40"
											>
												{pct}%
											</button>
										))
									)}
								</div>
							</div>
						</div>

						{/* The audited machinery, rendered as this box's own invoice
						    and confirm stage — same engine, same guards, no module
						    switch, and never a signature on the first click. */}
						<SwapPanel
							sellAmount={prefill?.sell}
							buyAmount={prefill?.buy}
							footClassName="mt-3"
							reviewLabel={side === 'buy' ? sideLabels(asset?.id).Buy : sideLabels(asset?.id).Sell}
							reviewRequest={reviewRequest}
							onReviewChange={setReviewOpen}
							onBroadcast={(info) => {
								setDone({ info, usd: usdValue })
								// The trade is spent: closing the receipt must land on an
								// EMPTY box, not the amount that was just bought.
								setRaw('')
							}}
						/>
					</React.Fragment>
				)}
			</div>

			{asset && <TwetchDoor context={{ id: asset.id, symbol }} compact />}
			{renderAboutCard()}
			{renderPositionsList((id) => navigate('home', id))}
		</div>
	)

	function renderAboutCard() {
		if (!asset) {
			return null
		}
		const buys = stats?.txns_buy_24h ?? null
		const sells = stats?.txns_sell_24h ?? null
		const buyVol = stats?.volume_buy_24h_usd ?? null
		const sellVol = stats?.volume_sell_24h_usd ?? null
		const ratio = (a: number, b: number) => (a + b > 0 ? (a / (a + b)) * 100 : 50)
		return (
			<div className="rounded-2xl bg-card p-4" data-testid="alkanes-about-card">
				<h3 className="text-sm font-semibold">About {symbol}</h3>
				<div className="mt-2.5 flex gap-2">
					{(
						[
							['24H', stats?.change_24h_pct ?? asset.change_24h_pct],
							['1W', stats?.change_7d_pct ?? null]
						] as const
					).map(([label, v]) => (
						<div
							key={label}
							className={cn(
								'flex flex-1 flex-col items-center gap-0.5 rounded-xl p-2',
								v === null ? 'bg-secondary/40' : v >= 0 ? 'bg-success/10' : 'bg-destructive/10'
							)}
						>
							<span className="text-xxs text-muted-foreground">{label}</span>
							<Change v={v} className="text-xs font-semibold" />
						</div>
					))}
				</div>
				{buys !== null && sells !== null && (
					<div className="mt-3 space-y-2">
						<BarRow
							left={`${buys} ${sideLabels(asset.id).buys}`}
							right={`${sells} ${sideLabels(asset.id).sells}`}
							pct={ratio(buys, sells)}
						/>
						{buyVol !== null && sellVol !== null && (
							<BarRow
								left={`${usdCompact(buyVol)} vol.`}
								right={`${usdCompact(sellVol)} vol.`}
								pct={ratio(buyVol, sellVol)}
							/>
						)}
					</div>
				)}
				<dl className="mt-3 space-y-1.5 border-t border-border/60 pt-2.5 text-xs">
					<div className="flex justify-between">
						<dt className="text-muted-foreground">Supply</dt>
						<dd className="tabular-nums">
							{stats?.supply ? formatAmount(BigInt(stats.supply), 0) : '—'}
						</dd>
					</div>
					<div className="flex justify-between">
						<dt className="text-muted-foreground">Network</dt>
						<dd>Bitcoin · Alkanes</dd>
					</div>
					<div className="flex justify-between">
						<dt className="text-muted-foreground">Id</dt>
						<dd className="tabular-nums">{asset.id}</dd>
					</div>
				</dl>
			</div>
		)
	}

	function renderPositionsList(onNavigate: (id: string) => void) {
		if (!loggedIn || balanceRows.length === 0) {
			return null
		}
		const enriched = sortByUsdValue(
			balanceRows.map((r) => ({
				...r,
				usd: balancesUsd.get(r.token.id) ?? null
			}))
		)
		const dust = enriched.filter((r) => r.usd !== null && r.usd < 1)
		const main = enriched.filter((r) => !(r.usd !== null && r.usd < 1))
		const shown = showDust ? enriched : main
		return (
			<div className="rounded-2xl bg-card p-3" data-testid="alkanes-positions-list">
				<div className="flex items-baseline justify-between px-1 pb-1">
					<h3 className="text-sm font-semibold">
						Your positions <span className="text-muted-foreground">{main.length}</span>
					</h3>
					<span className="rounded-full bg-secondary px-2 py-0.5 text-xxs font-semibold text-foreground">
						Open
					</span>
				</div>
				<ul>
					{shown.map((r) => {
						const isBtc = r.token.id === BTC_ID
						const open = expandedPosition === r.token.id
						return (
							<li key={r.token.id}>
								<div
									className={cn(
										'rounded-xl transition-colors',
										open ? 'bg-secondary/50' : 'hover:bg-secondary/40'
									)}
								>
									<div className="flex w-full items-center gap-2.5 p-2">
										<button
											type="button"
											onClick={() => onNavigate(r.token.id)}
											disabled={isBtc}
											className="flex min-w-0 flex-1 items-center gap-2.5 text-left disabled:cursor-default"
										>
											<TokenIcon token={r.token} size={28} />
											<span className="min-w-0 flex-1">
												<span className="block truncate text-xs font-bold">
													{r.token.name || r.token.id}
												</span>
												<span className="block truncate text-xxs tabular-nums text-muted-foreground">
													{formatAmount(r.raw, 4)}
												</span>
											</span>
											<span className="shrink-0 text-xs font-semibold tabular-nums">
												{r.usd !== null ? usdCompact(r.usd) : '—'}
											</span>
										</button>
										{!isBtc && (
											<button
												type="button"
												aria-expanded={open}
												aria-label={`Instant trade ${r.token.name || r.token.id}`}
												data-testid={`alkanes-position-expand-${r.token.id}`}
												onClick={() => setExpandedPosition(open ? null : r.token.id)}
												className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
											>
												<ChevronDown
													className={cn('size-4 transition-transform', open && 'rotate-180')}
													aria-hidden="true"
												/>
											</button>
										)}
									</div>
									{/* Instant actions: each chip files a trade INTENT — the
									    box fills the amount in BTC and the audited panel
									    still owns the frozen review and the signature. */}
									{open && !isBtc && (
										<div className="space-y-1.5 px-2 pb-2">
											<div className="flex gap-1.5">
												{BTC_QUICK.map((btc) => (
													<button
														key={btc}
														type="button"
														data-testid={`alkanes-instant-buy-${r.token.id}-${btc}`}
														onClick={() => {
															setTradeIntent({
																assetId: r.token.id,
																side: 'buy',
																btc: Number.parseFloat(btc)
															})
															onNavigate(r.token.id)
														}}
														className="h-8 flex-1 rounded-lg bg-success/15 text-xxs font-bold tabular-nums text-success-ink transition-colors hover:bg-success/25"
													>
														Buy ₿{btc}
													</button>
												))}
											</div>
											<div className="flex gap-1.5">
												{[10, 25, 50, 100].map((pct) => (
													<button
														key={pct}
														type="button"
														data-testid={`alkanes-instant-sell-${r.token.id}-${pct}`}
														onClick={() => {
															setTradeIntent({
																assetId: r.token.id,
																side: 'sell',
																pct
															})
															onNavigate(r.token.id)
														}}
														className="h-8 flex-1 rounded-lg bg-destructive/15 text-xxs font-bold tabular-nums text-destructive-ink transition-colors hover:bg-destructive/25"
													>
														Sell {pct}%
													</button>
												))}
											</div>
										</div>
									)}
								</div>
							</li>
						)
					})}
				</ul>
				{dust.length > 0 && (
					<button
						type="button"
						onClick={() => setShowDust((s) => !s)}
						data-testid="alkanes-show-dust"
						className="mt-1 w-full rounded-lg py-1.5 text-center text-xxs font-medium text-muted-foreground transition-colors hover:text-foreground"
					>
						{showDust ? 'Hide' : 'Show'} dust ({dust.length})
					</button>
				)}
			</div>
		)
	}
}

function BarRow({ left, right, pct }: { left: string; right: string; pct: number }) {
	return (
		<div>
			<div className="flex justify-between text-xs tabular-nums">
				<span className="text-success-ink">{left}</span>
				<span className="text-destructive-ink">{right}</span>
			</div>
			<div className="mt-1 flex h-1 gap-0.5 overflow-hidden rounded-full">
				<span className="rounded-full bg-success" style={{ width: `${pct}%` }} />
				<span className="flex-1 rounded-full bg-destructive" />
			</div>
		</div>
	)
}

/// Broadcast complete, in the rail's compact form: the same facts the phone
/// sheet's full-screen state carries.
function DoneCard({
	info,
	usd,
	side,
	onReset
}: {
	info: BroadcastInfo
	usd: number | null
	side: 'buy' | 'sell'
	onReset: () => void
}) {
	const verb =
		info.kind === 'wrap'
			? 'Wrapped'
			: info.kind === 'unwrap'
			? 'Unwrapping'
			: side === 'buy'
			? 'Bought'
			: 'Sold'
	return (
		<div
			className="flex flex-col items-center px-2 py-4 text-center"
			data-testid="alkanes-trade-done"
		>
			<span className="grid size-12 place-items-center rounded-full bg-success">
				<Check className="size-6 text-success-foreground" aria-hidden="true" />
			</span>
			<h3 className="mt-3 text-lg font-semibold">{verb}</h3>
			<p className="text-2xl font-bold tabular-nums">
				{usd !== null && usd > 0
					? `$${usd.toLocaleString('en-US', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
					  })}`
					: `${formatAmount(BigInt(info.amountOutRaw), 4)} ${info.outName}`}
			</p>
			<p className="mt-1 text-xs tabular-nums text-muted-foreground">
				{formatAmount(BigInt(info.amountInRaw), 4)} {info.sellName} →{' '}
				{info.kind === 'swap' ? '≈ ' : ''}
				{formatAmount(BigInt(info.amountOutRaw), 4)} {info.outName}
			</p>
			<p className="mt-2 font-mono text-xs tabular-nums text-muted-foreground">
				{info.txid.slice(0, 8)}…{info.txid.slice(-6)}
			</p>
			<div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-xs font-semibold text-primary">
				<a
					data-testid="alkanes-trade-espo-link"
					href={`${ESPO_URL}/tx/${info.txid}`}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1 hover:underline"
				>
					View on espo.sh
					<ExternalLink className="size-3.5" aria-hidden="true" />
				</a>
			</div>
			<button
				type="button"
				onClick={onReset}
				data-testid="alkanes-trade-done-close"
				className="mt-4 h-10 w-full rounded-xl bg-primary text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
			>
				Close receipt
			</button>
		</div>
	)
}
